import React, { useId } from "react";
import { ScallopedDisc, Sparkle } from "./Decor2";
import { imgUrl } from "../lib/api";

/* Non-warping wavy line — fixed-size repeating bumps via SVG pattern
 * (userSpaceOnUse keeps every bump identical on any screen width). */
export const WavyLine = ({ color = "#EC2675", className = "", bump = 26, strokeWidth = 2 }) => {
  const id = useId().replace(/:/g, "");
  return (
    <svg className={className} height="14" width="100%" style={{ display: "block" }} aria-hidden>
      <defs>
        <pattern id={`wv-${id}`} patternUnits="userSpaceOnUse" width={bump} height="14">
          <path
            d={`M0,7 Q${bump / 4},1 ${bump / 2},7 T${bump},7`}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </pattern>
      </defs>
      <rect width="100%" height="14" fill={`url(#wv-${id})`} />
    </svg>
  );
};

/* Solid bubble (scallop) edge — white filled semicircles that sit flush with
 * the page below and arch up into the coloured block above (or flipped). */
export const BubbleEdge = ({ color = "#FDFBF7", className = "", bump = 26, up = true }) => {
  const id = useId().replace(/:/g, "");
  const h = bump / 2 + 2;
  const d = up
    ? `M0,${h} L0,${h - 2} A${bump / 2},${bump / 2} 0 0 1 ${bump},${h - 2} L${bump},${h} Z`
    : `M0,0 L${bump},0 L${bump},2 A${bump / 2},${bump / 2} 0 0 0 0,2 Z`;
  return (
    <svg className={className} height={h} width="100%" style={{ display: "block" }} aria-hidden>
      <defs>
        <pattern id={`be-${id}`} patternUnits="userSpaceOnUse" width={bump} height={h}>
          <path d={d} fill={color} />
        </pattern>
      </defs>
      <rect width="100%" height={h} fill={`url(#be-${id})`} />
    </svg>
  );
};

/* Wavy page break used between every section. */
export const WavyBreak = ({ color = "#EC2675", bg = "bg-cream" }) => (
  <div className={`${bg} py-2`}>
    <div className="px-6 md:px-10 max-w-[1500px] mx-auto py-4 flex items-center gap-4">
      <WavyLine color={color} className="flex-1" />
      <Sparkle className="w-4 h-4 shrink-0" color={color} />
      <WavyLine color={color} className="flex-1" />
    </div>
  </div>
);

/* Masked, mobile-safe framed image:
 * coloured scalloped tile -> white scalloped frame -> circular crop mask
 * holding the photo neatly behind the decorative border. Container is
 * always square so the scallops never warp. */
export const FramedImage = ({
  src,
  alt = "",
  bg = "#FFC2D1",
  frame = "#FDFBF7",
  scallops = 26,
  contain = false,
  className = "",
  testid,
}) => (
  <div className={`relative aspect-square ${className}`} data-testid={testid}>
    <ScallopedDisc color={bg} scallops={scallops} className="absolute inset-0 w-full h-full" />
    <ScallopedDisc color={frame} scallops={scallops} className="absolute inset-[6%] w-[88%] h-[88%]" />
    <div className="absolute inset-[12%] w-[76%] h-[76%] rounded-full overflow-hidden flex items-center justify-center" style={contain ? { background: frame } : undefined}>
      <img
        src={imgUrl(src)}
        alt={alt}
        className={`w-full h-full ${contain ? "object-contain p-[6%]" : "object-cover"}`}
        draggable="false"
      />
    </div>
  </div>
);

/* Counter-rotating hero discs holding the logo — square + fluid = no warp. */
const wavyRect = (x0, y0, x1, y1, bump = 9) => {
  const seg = (edge) => Math.max(3, Math.round(edge / bump));
  const h = seg(x1 - x0); const hs = (x1 - x0) / h;
  const v = seg(y1 - y0); const vs = (y1 - y0) / v;
  const r = 0.62;
  let d = `M${x0},${y0} `;
  for (let i = 0; i < h; i++) d += `A${(hs * r).toFixed(2)},${(hs * r).toFixed(2)} 0 0 1 ${(x0 + (i + 1) * hs).toFixed(2)},${y0} `;
  for (let i = 0; i < v; i++) d += `A${(vs * r).toFixed(2)},${(vs * r).toFixed(2)} 0 0 1 ${x1},${(y0 + (i + 1) * vs).toFixed(2)} `;
  for (let i = 0; i < h; i++) d += `A${(hs * r).toFixed(2)},${(hs * r).toFixed(2)} 0 0 1 ${(x1 - (i + 1) * hs).toFixed(2)},${y1} `;
  for (let i = 0; i < v; i++) d += `A${(vs * r).toFixed(2)},${(vs * r).toFixed(2)} 0 0 1 ${x0},${(y1 - (i + 1) * vs).toFixed(2)} `;
  return d + "Z";
};

/* Scallop-edged rectangle photo frame — shows the full-ish image without circle cropping */
export const WavyCard = ({ src, alt = "", bg = "#FFC2D1", frame = "#FDFBF7", contain = false, className = "", testid }) => {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <svg viewBox="0 0 100 120" className="w-full h-auto drop-shadow-[0_18px_30px_rgba(74,27,77,0.18)]" role="img" aria-label={alt}>
        <path d={wavyRect(4, 4, 96, 116, 9)} fill={bg} />
        <path d={wavyRect(8.5, 8.5, 91.5, 111.5, 8)} fill={frame} />
        <clipPath id={`wc${uid}`}>
          <rect x="12.5" y="12.5" width="75" height="95" rx="4" />
        </clipPath>
        {contain && <rect x="12.5" y="12.5" width="75" height="95" rx="4" fill={frame} />}
        <image
          href={imgUrl(src)}
          x="12.5" y="12.5" width="75" height="95"
          preserveAspectRatio={contain ? "xMidYMid meet" : "xMidYMid slice"}
          clipPath={`url(#wc${uid})`}
        />
      </svg>
    </div>
  );
};

export const RotatingLogo = ({ src, alt = "", className = "", testid }) => (
  <div className={`relative w-full aspect-square ${className}`} data-testid={testid}>
    <div className="absolute inset-0 animate-spin-slow">
      <ScallopedDisc color="#EC2675" scallops={32} className="w-full h-full drop-shadow-[0_40px_70px_rgba(74,27,77,0.4)]" />
    </div>
    <div className="absolute inset-[7%] animate-spin-slow-rev">
      <ScallopedDisc color="#4ECDE9" scallops={28} className="w-full h-full" />
    </div>
    <div className="absolute inset-[15%] rounded-full bg-cream shadow-inner" />
    <img src={imgUrl(src)} alt={alt} className="absolute inset-[16%] w-[68%] h-[68%] object-contain z-10" draggable="false" />
  </div>
);
